import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { BarcodeDetectionResult } from 'services/camera-strategy';
import { QuaggaProcessResult } from 'services/barcode-decoder.service';

/** Default format set understood by the web decoder (Quagga's `upc_reader` +
 *  `ean_reader`), matching the food-lookup backend's supported codes. */
export const DEFAULT_BARCODE_FORMATS = ['ean13', 'ean8', 'upca', 'upce'];

/**
 * Marker directive the barcode-scanner page projects into `<cs-camera-view>`.
 * Presence of a projected `<cs-barcode-detector>` signals to `cs-camera-view`
 * (via `@ContentChild`) that:
 *
 * 1. The viewport needs a barcode-capable strategy. If the factory's preferred
 *    strategy doesn't support barcode detection, the component falls back to
 *    `WebCameraStrategy` for that instance.
 * 2. Detection should be wired up automatically after the camera is ready —
 *    `cs-camera-view` calls `strategy.startBarcodeDetection(...)` with the
 *    directive's `types` and forwards results to `(detected)` / `(processed)`.
 *
 * The directive itself has no template or DOM footprint.
 */
@Directive({
  selector: 'cs-barcode-detector',
  standalone: true,
})
export class CsBarcodeDetectorDirective {
  /** Barcode formats to decode. Web (Quagga) currently ignores this and always
   *  decodes `['ean13','ean8','upca','upce']`; Phase B iOS native honors it. */
  @Input() types: string[] = DEFAULT_BARCODE_FORMATS;

  /** Confirmed-detection emitter. Cadence depends on strategy:
   *   - **Web (Quagga + bracket-tracker overlay)**: fires per gated
   *     selector pick — i.e. once per Quagga frame after the first-morph
   *     gate opens, with the gate buffering and flushing on the
   *     overlay's first idle→detected morph completion. Quagga's own
   *     `onDetected` callback is intentionally unused; the smoothed
   *     selector pick replaces it.
   *   - **Native (iOS/Android)**: fires per native-decoder confirmation
   *     (AVFoundation `metadataOutput`, ML Kit `processImage`), gated
   *     natively by each platform's overlay first-morph contract.
   *  The host page (barcode-scanner-page) still applies its own
   *  successive-match heuristic on `(processed)` before acting on a
   *  code, so this output's cadence change is not user-visible. */
  @Output() detected = new EventEmitter<BarcodeDetectionResult>();

  /** Per-frame raw decoder result (Quagga-shaped on web). The barcode-scanner
   *  page uses it to run false-positive heuristics and draw its own overlay
   *  into `Quagga.canvas`. Not emitted on native strategies — Phase B exposes
   *  a native equivalent if drawing there becomes necessary. */
  @Output() processed = new EventEmitter<QuaggaProcessResult>();

  /** Push a detection through to the host's `(detected)` listener. Called by
   *  `cs-camera-view` from the strategy-level detection callback. */
  emitDetected(result: BarcodeDetectionResult) {
    this.detected.emit(result);
  }

  /** Push a per-frame result through to `(processed)`. */
  emitProcessed(result: QuaggaProcessResult) {
    this.processed.emit(result);
  }
}
